import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import styles from './MentionList.module.scss'

interface MentionListProps {
  items: Array<{
    id: number
    name: string
    email: string
    role: string
  }>
  command: (item: { id: number; label: string }) => void
  loading?: boolean
}

const MentionList = forwardRef((props: MentionListProps, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items[index]

    if (item) {
      props.command({ id: item.id, label: item.name })
    }
  }

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  }

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length)
  }

  const enterHandler = () => {
    selectItem(selectedIndex)
  }

  useEffect(() => setSelectedIndex(0), [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }

      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }

      if (event.key === 'Enter') {
        enterHandler()
        return true
      }

      return false
    },
  }))

  return (
    <div className={styles.mentionList}>
      {props.loading ? (
        <div className={`${styles.mentionItem} ${styles.loading}`}>
          <div className={styles.spinner}></div>
          <span>Searching...</span>
        </div>
      ) : props.items.length ? (
        props.items.map((item, index) => (
          <button
            className={`${styles.mentionItem} ${index === selectedIndex ? styles.isSelected : ''}`}
            key={item.id}
            onClick={() => selectItem(index)}
            type="button"
          >
            <div className={styles.mentionItemContent}>
              <div className={styles.mentionItemName}>{item.name}</div>
              <div className={styles.mentionItemDetails}>
                <span className={styles.mentionItemRole}>{item.role}</span>
                <span className={styles.mentionItemEmail}>{item.email}</span>
              </div>
            </div>
          </button>
        ))
      ) : (
        <div className={`${styles.mentionItem} ${styles.noResults}`}>No results</div>
      )}
    </div>
  )
})

MentionList.displayName = 'MentionList'

export { MentionList }
